/**
 * Tache 1293 — Qui paie quoi : la cle du client, ou la notre.
 *
 * L'historique anterieur au marquage est affiche « source inconnue » : on
 * prefere l'admettre plutot que de deviner d'apres la configuration actuelle,
 * qui a pu changer entre-temps.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE as API } from "../../lib/apiBase";

export const CostByOrgCard = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/byok/cost-by-org?days=${days}`,
          { withCredentials: true, timeout: 60000 });
        if (!annule) { setData(d); setError(""); }
      } catch (e) {
        if (!annule) setError(e?.response?.data?.detail || "Lecture impossible");
      }
    })();
    return () => { annule = true; };
  }, [days]);

  if (error) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="cost-by-org-card">
        <h3 className="text-sm font-bold text-zinc-900">Coût par organisation</h3>
        <p className="text-xs text-rose-700 mt-2">{String(error)}</p>
      </div>
    );
  }

  const orgs = data?.orgs || [];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="cost-by-org-card">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-bold text-zinc-900">Coût par organisation — clé du client ou la nôtre</h3>
          <p className="text-xs text-zinc-600 mt-1 max-w-xl">
            Ce qui est payé par la clé de l'organisation, et ce qui retombe sur la
            clé Knoria. Un montant qui glisse vers « Knoria » signale une clé
            cliente illisible, en pause, ou un forçage volontaire.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="self-start text-xs border border-zinc-300 rounded-lg px-2 py-1.5"
          data-testid="cost-by-org-period"
        >
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
        </select>
      </div>

      {!data && <p className="text-xs text-zinc-500 mt-3">Lecture…</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4" data-testid="cost-by-org-totals">
            <Chiffre label="Payé par les clients" value={`${data.byok_paid_usd} USD`} tone="teal" />
            <Chiffre label="Payé par Knoria" value={`${data.knoria_paid_usd} USD`}
                     tone={data.knoria_paid_usd > 0 ? "rose" : "zinc"} />
            <Chiffre label="Source inconnue" value={`${data.unknown_usd} USD`} tone="amber" />
          </div>

          {orgs.length === 0 && (
            <p className="text-xs text-zinc-500 mt-3" data-testid="cost-by-org-empty">
              Aucun appel IA sur la période.
            </p>
          )}

          {orgs.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs" data-testid="cost-by-org-table">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">Organisation</th>
                    <th className="py-2 pr-3 text-right">Total</th>
                    <th className="py-2 pr-3 text-right">Sa clé</th>
                    <th className="py-2 pr-3 text-right">Clé Knoria</th>
                    <th className="py-2 pr-3 text-right">Inconnu</th>
                    <th className="py-2 text-right">Appels</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => (
                    <tr key={o.org_id} className="border-b border-zinc-100"
                        data-testid={`cost-by-org-row-${o.org_id}`}>
                      <td className="py-2 pr-3 text-zinc-800">{o.org_name}</td>
                      <td className="py-2 pr-3 text-right font-medium">{o.total_usd}</td>
                      <td className="py-2 pr-3 text-right text-teal-700">{o.byok_usd}</td>
                      <td className={`py-2 pr-3 text-right ${o.knoria_usd > 0 ? "text-rose-700 font-bold" : "text-zinc-400"}`}>
                        {o.knoria_usd}
                      </td>
                      <td className="py-2 pr-3 text-right text-amber-700">{o.unknown_usd}</td>
                      <td className="py-2 text-right text-zinc-500">{o.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-zinc-500 mt-3">{data.note}</p>
        </>
      )}
    </div>
  );
};

const Chiffre = ({ label, value, tone }) => {
  const couleurs = {
    teal: "bg-teal-50 border-teal-100 text-teal-800",
    rose: "bg-rose-50 border-rose-100 text-rose-800",
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    zinc: "bg-zinc-50 border-zinc-200 text-zinc-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${couleurs[tone] || couleurs.zinc}`}>
      <div className="text-[11px] opacity-80">{label}</div>
      <div className="text-base font-bold">{value}</div>
    </div>
  );
};

export default CostByOrgCard;
